import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiFailure, authApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from '@/components/ui/AuthShell';
import { Button } from '@/components/ui/Button';
import { Input, Label, FieldHint } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';

/**
 * Page de changement forcé de mot de passe - affichée quand le backend
 * retourne `PASSWORD_CHANGE_REQUIRED` (SECURITY.md §1.2.1).
 *
 * Règles côté UI :
 *  - Exige le mot de passe temporaire courant + nouveau + confirmation.
 *  - Vérifie la politique (12 chars + complexité) côté client pour feedback
 *    immédiat ; le backend est la source de vérité (refuse avec `details` Zod).
 *  - Après succès : clear le flag `passwordChangeRequired`, toast, redirect
 *    vers le dashboard.
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const passwordChangeRequired = useAuthStore((s) => s.passwordChangeRequired);
  const setPasswordChangeRequired = useAuthStore((s) => s.setPasswordChangeRequired);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pas de token → retour login. On ne montre cette page qu'à un user
  // authentifié dont la session est valide.
  if (!accessToken) return <Navigate to="/login" replace />;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (next !== confirm) {
      setError('Les deux nouveaux mots de passe ne correspondent pas.');
      return;
    }
    if (current === next) {
      setError('Le nouveau mot de passe doit être différent du précédent.');
      return;
    }
    const policyErr = checkPolicy(next);
    if (policyErr) {
      setError(policyErr);
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changePassword(accessToken!, current, next);
      setPasswordChangeRequired(false);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiFailure ? mapError(err) : 'Erreur réseau - merci de réessayer');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Changement du mot de passe"
      subtitle={
        passwordChangeRequired
          ? 'Votre mot de passe a été généré par un administrateur. Vous devez le remplacer avant de continuer.'
          : 'Définissez un nouveau mot de passe.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="current" required>
            Mot de passe actuel
          </Label>
          <Input
            id="current"
            name="current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="next" required>
            Nouveau mot de passe
          </Label>
          <Input
            id="next"
            name="next"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
          <FieldHint>
            12 caractères min. avec majuscule, minuscule, chiffre et caractère spécial.
          </FieldHint>
        </div>
        <div>
          <Label htmlFor="confirm" required>
            Confirmer le nouveau mot de passe
          </Label>
          <Input
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <Button type="submit" variant="primary" loading={submitting} className="w-full">
          Enregistrer
        </Button>
      </form>
    </AuthShell>
  );
}

function checkPolicy(p: string): string | null {
  if (p.length < 12) return 'Au moins 12 caractères requis.';
  if (!/[A-Z]/.test(p)) return 'Au moins une majuscule requise.';
  if (!/[a-z]/.test(p)) return 'Au moins une minuscule requise.';
  if (!/[0-9]/.test(p)) return 'Au moins un chiffre requis.';
  if (!/[^A-Za-z0-9]/.test(p)) return 'Au moins un caractère spécial requis.';
  return null;
}

function mapError(err: ApiFailure): string {
  if (err.code === 'UNAUTHORIZED') return 'Mot de passe actuel incorrect.';
  if (err.code === 'VALIDATION_ERROR') {
    const details = err.details as Array<{ field: string; message: string }> | undefined;
    const firstPolicyIssue = details?.find(
      (d) => d.field.includes('newPassword') || d.field === 'password',
    );
    return firstPolicyIssue?.message ?? err.message;
  }
  if (err.code === 'FORBIDDEN' && err.message.toLowerCase().includes('pwned')) {
    return 'Ce mot de passe a été compromis dans une fuite connue. Choisissez-en un autre.';
  }
  if (err.code === 'RATE_LIMITED') {
    return 'Trop de tentatives. Réessayez dans une heure.';
  }
  return err.message;
}
